import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/userAuth/UserAuthState";
import { FaEye, FaEyeSlash, FaSpinner, FaShieldAlt, FaArrowLeft } from "react-icons/fa";
import { MdError, MdInfo } from "react-icons/md";
import "bootstrap/dist/css/bootstrap.min.css";

// ─── Client-side validation ───────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = ({ email, password }) => {
  if (!email.trim())              return "Email address is required.";
  if (!EMAIL_REGEX.test(email))   return "Please enter a valid email address.";
  if (!password)                  return "Password is required.";
  if (password.length < 6)        return "Password must be at least 6 characters.";
  return null;
};

// ─── LoginPage ────────────────────────────────────────────────────────────────

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, showError, startLoading, clearError } = useAuth();

  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError]     = useState("");
  const [sessionMsg, setSessionMsg]     = useState("");

  // Pick up any session-expired message set by auto-logout
  useEffect(() => {
    const reason = sessionStorage.getItem("logout_reason");
    if (reason) {
      setSessionMsg(reason);
      sessionStorage.removeItem("logout_reason");
    }
  }, []);

  // Clear errors when user starts typing
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (localError) setLocalError("");
    if (showError)  clearError();
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (localError) setLocalError("");
    if (showError)  clearError();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSessionMsg("");

    // Client-side validation first
    const validationError = validate({ email, password });
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError("");
    const isSuccess = await login({ email: email.trim(), password });
    if (isSuccess) {
      setEmail("");
      setPassword("");
      navigate("/dashboard", { replace: true });
    }
  };

  // Active error — local validation takes priority over server error
  const activeError = localError || showError;

  return (
    <div className="lp-page">

      {/* ── Navbar ─────────────────────────────────────────────── */}
      <nav className="lp-navbar">
        <div className="lp-navbar__inner">
          <Link to="/" className="lp-navbar__brand">
            <img
              src="/assets/logo/color_logo.png"
              alt="TUV PAK"
              className="lp-navbar__logo"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <span className="lp-navbar__name">TUV PAK</span>
          </Link>
          <Link to="/" className="lp-navbar__back">
            <FaArrowLeft size={13} />
            Back to Verification
          </Link>
        </div>
      </nav>

      {/* ── Main ───────────────────────────────────────────────── */}
      <main className="lp-main">
        <div className="lp-card">

          {/* Card header */}
          <div className="lp-card__header">
            <div className="lp-card__icon">
              <FaShieldAlt size={26} color="#fff" />
            </div>
            <h1 className="lp-card__title">Admin Sign In</h1>
            <p className="lp-card__subtitle">
              TUV PAK Inspec — Management Portal
            </p>
          </div>

          {/* Session expired / logged out notice */}
          {sessionMsg && (
            <div className="lp-alert lp-alert--info" role="alert">
              <MdInfo size={18} />
              <span>{sessionMsg}</span>
            </div>
          )}

          {/* Error alert */}
          {activeError && (
            <div className="lp-alert lp-alert--error" role="alert" aria-live="polite">
              <MdError size={18} />
              <span>{activeError}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin} noValidate>

            {/* Email */}
            <div className="lp-field">
              <label htmlFor="lp-email" className="lp-label">
                Email Address
              </label>
              <input
                id="lp-email"
                type="email"
                className={`lp-input${activeError && !password ? " lp-input--error" : ""}`}
                placeholder="admin@tuvpak.com"
                value={email}
                onChange={handleEmailChange}
                autoComplete="email"
                required
                aria-describedby={activeError ? "lp-error" : undefined}
              />
            </div>

            {/* Password */}
            <div className="lp-field">
              <label htmlFor="lp-password" className="lp-label">
                Password
              </label>
              <div className="lp-input-wrap">
                <input
                  id="lp-password"
                  type={showPassword ? "text" : "password"}
                  className="lp-input lp-input--password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="lp-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="lp-submit"
              disabled={startLoading}
              aria-busy={startLoading}
            >
              {startLoading ? (
                <>
                  <FaSpinner className="lp-spin" size={16} />
                  Signing in…
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Footer note */}
          <p className="lp-card__note">
            This portal is for authorised TUV PAK administrators only.
            <br />
            Session expires after <strong>24 hours</strong>.
          </p>
        </div>
      </main>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="lp-footer">
        © {new Date().getFullYear()} TUV PAK Inspection Services. All rights reserved.
      </footer>
    </div>
  );
};

export default LoginPage;
